import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

// 🌸 Bandeau discret d'attente pour le lancement Baby & Mom Planner (Élodie #773 next-action)
//    Toujours visible sur la home dashboard jusqu'au 26 juillet 2026 00:00 Paris.
//    Après la date, il disparaît automatiquement.
const LAUNCH_DATE = new Date('2026-07-25T22:00:00Z'); // 2026-07-26 00:00 Europe/Paris (UTC+2 été)

export default function PlannerLaunchTeaser() {
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  if (now.getTime() >= LAUNCH_DATE.getTime()) return null;

  const diffMs = LAUNCH_DATE.getTime() - now.getTime();
  const totalHours = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60)));
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;
  const minutes = Math.max(0, Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60)));

  const showHoursMode = days === 0;

  return (
    <button
      type="button"
      onClick={() => navigate('/dashboard/baby-planner')}
      className="w-full mb-4 relative overflow-hidden rounded-2xl border border-[#F5DDE5] bg-gradient-to-r from-[#FFF0F5] via-[#FDF5FF] to-[#F0E5FF] px-4 py-3 shadow-sm active:scale-[0.99] transition-transform text-left group"
      data-testid="planner-launch-teaser"
      aria-label="Découvrir Baby & Mom Planner"
    >
      {/* Petits emojis flottants en arrière-plan (opacité douce) */}
      <div className="absolute inset-0 pointer-events-none opacity-20 select-none" aria-hidden="true">
        <span className="absolute text-lg" style={{ top: '8%', right: '18%', animation: 'teaser-float 6s ease-in-out infinite' }}>✨</span>
        <span className="absolute text-base" style={{ bottom: '12%', right: '32%', animation: 'teaser-float 8s ease-in-out infinite 1.5s' }}>🌸</span>
      </div>
      <style>{`@keyframes teaser-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-4px)} }`}</style>

      <div className="relative flex items-center gap-3">
        {/* Duo d'emojis planners */}
        <div className="flex-shrink-0 relative w-11 h-11">
          <span
            className="absolute left-0 top-0.5 text-2xl drop-shadow-sm"
            style={{ animation: 'teaser-float 5s ease-in-out infinite' }}
          >
            👶
          </span>
          <span
            className="absolute right-0 bottom-0 text-2xl drop-shadow-sm"
            style={{ animation: 'teaser-float 5s ease-in-out infinite 1s' }}
          >
            🌸
          </span>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-0.5">
            <Sparkles className="w-3 h-3 text-[#F5A5C4]" />
            <span className="text-[9px] font-black uppercase tracking-widest text-[#B399E6]">Bientôt · Le 26 juillet</span>
          </div>
          <p className="text-[13px] font-bold text-[#5C3D36] leading-snug">
            Baby &amp; Mom Planner arrivent{' '}
            <span className="bg-gradient-to-r from-[#F5A5C4] to-[#B399E6] bg-clip-text text-transparent">
              ✨
            </span>
          </p>
        </div>

        {/* Compte à rebours compact */}
        <div className="flex-shrink-0 flex items-center gap-1 pl-2 border-l border-[#F5DDE5]" data-testid="planner-launch-teaser-countdown">
          {showHoursMode ? (
            <>
              <div className="text-center">
                <div className="text-lg font-black text-[#F5A5C4] tabular-nums leading-none" data-testid="teaser-hours">{String(hours).padStart(2, '0')}</div>
                <div className="text-[7px] text-[#9D8189] font-bold uppercase tracking-wide mt-0.5">h</div>
              </div>
              <div className="text-sm text-[#F5A5C4] font-black">:</div>
              <div className="text-center">
                <div className="text-lg font-black text-[#F5A5C4] tabular-nums leading-none" data-testid="teaser-minutes">{String(minutes).padStart(2, '0')}</div>
                <div className="text-[7px] text-[#9D8189] font-bold uppercase tracking-wide mt-0.5">min</div>
              </div>
            </>
          ) : (
            <div className="text-center px-1">
              <div className="text-xl font-black text-[#F5A5C4] tabular-nums leading-none" data-testid="teaser-days">J-{days}</div>
              <div className="text-[8px] text-[#9D8189] font-bold uppercase tracking-wider mt-1">jour{days > 1 ? 's' : ''}</div>
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
